import { type FC } from 'react';
import {
  Loader2Icon,
  CheckCircle2Icon,
  XCircleIcon,
  BriefcaseIcon,
} from 'lucide-react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { cn } from '@/lib/utils';

interface ListUseCasesToolCallProps {
  toolName: string;
  toolCallId: string;
  args: any;
  argsText: string;
  result?: string;
  status?: { type: 'running' | 'complete' | 'incomplete'; reason?: string };
}

function parseResult(result?: string): { success?: boolean; error?: string; data: any } {
  if (result === undefined) return { data: {} };
  try {
    const parsed = typeof result === 'string' ? JSON.parse(result) : result;
    return {
      success: parsed?.success,
      error: parsed?.error,
      data: parsed,
    };
  } catch {
    return { data: {} };
  }
}

const STATUS_VARIANTS: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  APPROVED: 'default',
  IMPLEMENTATION_COMPLETE: 'default',
  PENDING: 'outline',
  CONNECTED: 'secondary',
  VALIDATING: 'secondary',
  IMPLEMENTATION_IN_PROGRESS: 'secondary',
  CHANGES_REQUESTED: 'destructive',
  PLAN_APPROVED: 'secondary',
};

const ListUseCasesToolCallComponent: FC<ListUseCasesToolCallProps> = ({ result, status }) => {
  const isRunning = status?.type === 'running' || (status === undefined && result === undefined);
  const isError = status?.type === 'incomplete' && status.reason === 'error';
  const isComplete = status?.type === 'complete' && !isError;

  const { error: resultError, data } = parseResult(result);
  const hasError = isError || (result !== undefined && data?.success === false) || !!resultError;

  const useCases = data?.use_cases ?? [];

  return (
    <Card>
      <div className={cn(
        'p-3',
        isRunning ? 'bg-primary/10 border-b border-primary/20' : hasError ? 'bg-destructive/10' : 'bg-muted/50',
      )}>
        {isRunning ? (
          <div className="flex items-center gap-2">
            <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
            <p className="text-sm m-0 text-foreground">Loading use cases...</p>
          </div>
        ) : hasError ? (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <BriefcaseIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <p className="text-sm m-0 text-muted-foreground">Use Cases</p>
            </div>
            <div className="flex items-center gap-2">
              <XCircleIcon className="h-4 w-4 text-destructive flex-shrink-0" />
              <p className="text-sm m-0 text-destructive">
                {resultError || 'Failed to load use cases'}
              </p>
            </div>
          </div>
        ) : isComplete ? (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <BriefcaseIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <p className="text-sm m-0 text-muted-foreground">Use Cases</p>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2Icon className="h-4 w-4 text-success flex-shrink-0" />
              <p className="text-sm m-0 text-foreground">
                {useCases.length === 0
                  ? 'No use cases found.'
                  : `Found ${useCases.length} use case${useCases.length !== 1 ? 's' : ''}`}
              </p>
            </div>
            {useCases.length > 0 && (
              <div className="ml-6 mt-1 space-y-2">
                {useCases.map((uc: any) => (
                  <div key={uc.id} className="space-y-0.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-foreground font-medium">{uc.name}</span>
                      <Badge
                        variant={STATUS_VARIANTS[uc.data_modeling_status] || 'outline'}
                        className="text-[10px] py-0 px-1.5"
                      >
                        {(uc.data_modeling_status || 'PENDING').replace(/_/g, ' ')}
                      </Badge>
                    </div>
                    {uc.data_sources?.length > 0 && (
                      <div className="ml-4 space-y-0.5">
                        {uc.data_sources.map((ds: any) => (
                          <div key={ds.id} className="flex items-center gap-2 flex-wrap">
                            <span className="text-[11px] text-muted-foreground">{ds.name}</span>
                            <Badge
                              variant={STATUS_VARIANTS[ds.status] || 'outline'}
                              className="text-[10px] py-0 px-1.5"
                            >
                              {(ds.status || 'PENDING').replace(/_/g, ' ')}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
            <p className="text-sm m-0 text-foreground">Loading use cases...</p>
          </div>
        )}
      </div>
    </Card>
  );
};

export const ListUseCasesToolCall = ListUseCasesToolCallComponent as React.ComponentType<any>;
